/**
 * `edodo-write/react` — a thin React wrapper over the framework-free editor.
 *
 *   import { EdodoWriteEditor, Markdown } from "edodo-write/react";
 *   import "edodo-write/styles.css";
 *
 *   <EdodoWriteEditor value={md} onChange={setMd} placeholder="Write…" />
 *   <Markdown value={md} />   // read-only render
 *
 * React is a peer dependency; the core entry never imports it.
 */

import { useEffect, useMemo, useRef } from "react";
import type { CSSProperties } from "react";
import { EdodoWrite } from "../core/editor.js";
import type { EditorOptions, EdodoPlugin, SelectionInfo } from "../core/types.js";
import { parseMarkdown } from "../core/parse.js";
import { createRenderCodec } from "./index.js";

export interface EdodoWriteEditorProps
  extends Omit<EditorOptions, "value" | "onChange" | "className"> {
  /** Markdown value. Treated as "initial + controlled": external changes that
   *  differ from the last emitted value re-hydrate the editor. */
  value?: string;
  onChange?: (markdown: string) => void;
  onSelection?: (info: SelectionInfo | null) => void;
  /** The `submitOn` key was pressed (see EditorOptions.submitOn). Latest-ref
   *  like onChange: the handler may close over the host's current state. */
  onSubmit?: (editor: EdodoWrite) => void;
  /** Imperative handle to the underlying editor, once mounted. */
  onReady?: (editor: EdodoWrite) => void;
  className?: string;
  style?: CSSProperties;
}

export function EdodoWriteEditor({
  value = "",
  onChange,
  onSelection,
  onSubmit,
  onReady,
  className,
  style,
  ...opts
}: EdodoWriteEditorProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<EdodoWrite | null>(null);
  const lastValueRef = useRef<string>(value);
  const onChangeRef = useRef(onChange);
  const onSelectionRef = useRef(onSelection);
  const onSubmitRef = useRef(onSubmit);
  onChangeRef.current = onChange;
  onSelectionRef.current = onSelection;
  onSubmitRef.current = onSubmit;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const editor = new EdodoWrite(host, { ...opts, value });
    editorRef.current = editor;
    lastValueRef.current = value;

    const offChange = editor.on("change", (md) => {
      lastValueRef.current = md;
      onChangeRef.current?.(md);
    });
    const offSelection = editor.on("selection", (info) => onSelectionRef.current?.(info));
    const offSubmit = editor.on("submit", () => onSubmitRef.current?.(editor));
    onReady?.(editor);

    return () => {
      offChange();
      offSelection();
      offSubmit();
      editor.destroy();
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Controlled-value sync: only re-hydrate when the incoming value differs from
  // what the editor last emitted, so we never clobber the caret while typing.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      editor.setMarkdown(value, { silent: true });
    }
  }, [value]);

  return <div ref={hostRef} className={className} style={style} />;
}

export interface MarkdownProps {
  value?: string;
  /** Plugin set to render with. When given, routes through the plugin-aware
   *  render codec (mentions/emoji/callouts/math render as their HTML) instead
   *  of the bare GFM parser — the same codec an editor built with these plugins
   *  uses, so read-only render matches the editor byte-for-byte. */
  plugins?: EdodoPlugin[];
  className?: string;
  style?: CSSProperties;
}

/** Read-only Markdown renderer sharing the editor's stylesheet. */
export function Markdown({ value = "", plugins, className, style }: MarkdownProps) {
  // Build the plugin-aware codec once per plugin set. Rendering is
  // deterministic (no random ids), so the SSR string and the client render
  // agree and `dangerouslySetInnerHTML` hydrates without a warning.
  const codec = useMemo(
    () => (plugins && plugins.length ? createRenderCodec(plugins) : null),
    [plugins],
  );
  const html = codec ? codec.render(value) : parseMarkdown(value);
  return (
    <div className={className ? `ew ${className}` : "ew"} style={style}>
      <div className="ew-content ew-content--readonly" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

export { EdodoWrite } from "../core/editor.js";
export type { EditorOptions, SelectionInfo } from "../core/types.js";
